import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import { useSpvTrustList } from "@/hooks/capitalMarkets/useSpvTrusts";
import { useSpvPoolList } from "@/hooks/capitalMarkets/useSpvPools";
import type { SpvDto } from "@/types/spvPool";
import { TrustStatusBadge } from "../shared/components";
import { formatDate } from "../shared/format";

export function TrustListPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("Spv.Manage");

  const trustsQuery = useSpvTrustList({ pageSize: 200 });
  const poolsQuery = useSpvPoolList({ pageSize: 200 });

  const trusts = trustsQuery.data?.data ?? [];

  const poolCount = useMemo(() => {
    const map = new Map<string, number>();
    for (const p of poolsQuery.data?.data ?? []) {
      map.set(p.spvId, (map.get(p.spvId) ?? 0) + 1);
    }
    return map;
  }, [poolsQuery.data]);

  const columns: Column<SpvDto>[] = useMemo(
    () => [
      {
        key: "trustName",
        header: "Trust Name",
        render: (t) => (
          <Link
            to={`/capital-markets/trusts/${t.id}`}
            className="font-semibold hover:text-gold-shadow"
          >
            {t.trustName}
          </Link>
        ),
      },
      {
        key: "ein",
        header: "EIN",
        render: (t) => (
          <span className="font-mono text-caption">{t.einMasked ?? "—"}</span>
        ),
      },
      {
        key: "status",
        header: "Status",
        render: (t) => <TrustStatusBadge status={t.status} />,
      },
      {
        key: "formation",
        header: "Formation Date",
        render: (t) => formatDate(t.formationDate),
      },
      {
        key: "trustee",
        header: "Trustee",
        render: (t) => (
          <span className="font-mono text-caption text-gray-mid">
            {t.indentureTrusteeId?.slice(0, 8) ?? "—"}
          </span>
        ),
      },
      {
        key: "trustCo",
        header: "Trust Company",
        render: (t) => (
          <span className="font-mono text-caption text-gray-mid">
            {t.trustCompanyId?.slice(0, 8) ?? "—"}
          </span>
        ),
      },
      {
        key: "backup",
        header: "Backup Servicer",
        render: (t) => (
          <span className="font-mono text-caption text-gray-mid">
            {t.backupServicerId?.slice(0, 8) ?? "—"}
          </span>
        ),
      },
      {
        key: "pools",
        header: "# Pools",
        render: (t) => poolCount.get(t.id) ?? 0,
      },
      {
        key: "actions",
        header: "",
        className: "w-10 text-right",
        render: (t) => (
          <Link
            to={`/capital-markets/trusts/${t.id}`}
            className="text-caption text-gold-shadow font-display uppercase tracking-wide hover:underline"
          >
            View
          </Link>
        ),
      },
    ],
    [poolCount],
  );

  return (
    <>
      <PageHeader
        eyebrow="Capital Markets"
        title="SPV Trusts"
        description="Special-purpose vehicle trust entities that own SPV pools."
        actions={
          canManage && (
            <Link to="/capital-markets/trusts/new">
              <Button>
                <Plus size={16} strokeWidth={2} aria-hidden /> New Trust
              </Button>
            </Link>
          )
        }
      />
      <DataTable
        columns={columns}
        rows={trusts}
        loading={trustsQuery.isLoading}
        error={trustsQuery.isError ? "Could not load SPV trusts." : null}
        emptyMessage="No SPV trusts yet. Create your first trust to begin."
        rowKey={(t) => t.id}
      />
    </>
  );
}
